import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Plus, Edit, Trash2, X, ListPlus, ChevronDown, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useAnnouncementListings } from "@/hooks/useAnnouncementListings";
import ListingCard from "./ListingCard";

interface Announcement {
  id: string;
  title: string;
  description: string | null;
  auction_date: string | null;
  created_at: string;
  updated_at: string;
}

interface AnnouncementsSectionProps {
  subscriptionActive: boolean;
  authLoading: boolean;
}

export const AnnouncementsSection: React.FC<AnnouncementsSectionProps> = ({ subscriptionActive, authLoading }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", description: "", auction_date: "" });
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [announcementLoading, setAnnouncementLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchAnnouncements();
    }
  }, [user]);

  const fetchAnnouncements = async () => {
    try {
      const { data, error } = await supabase
        .from("announcements")
        .select("*")
        .eq("breeder_id", user?.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAnnouncements(data || []);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast({
        title: "Error",
        description: "Failed to load announcements",
        variant: "destructive",
      });
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title.trim()) {
      toast({
        title: "Error",
        description: "Title is required",
        variant: "destructive",
      });
      return;
    }

    setAnnouncementLoading(true);
    try {
      const { data, error } = await supabase
        .from("announcements")
        .insert([
          {
            breeder_id: user?.id,
            title: newAnnouncement.title,
            description: newAnnouncement.description || null,
            auction_date: newAnnouncement.auction_date || null,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      setAnnouncements([data, ...announcements]);
      setNewAnnouncement({ title: "", description: "", auction_date: "" });
      setShowAnnouncementForm(false);
      toast({
        title: "Success",
        description: "Announcement created successfully",
      });
    } catch (error) {
      console.error("Error creating announcement:", error);
      toast({
        title: "Error",
        description: "Failed to create announcement",
        variant: "destructive",
      });
    } finally {
      setAnnouncementLoading(false);
    }
  };

  const handleUpdateAnnouncement = async () => {
    if (!editingAnnouncement || !editingAnnouncement.title.trim()) {
      toast({
        title: "Error",
        description: "Title is required",
        variant: "destructive",
      });
      return;
    }

    setAnnouncementLoading(true);
    try {
      const { data, error } = await supabase
        .from("announcements")
        .update({
          title: editingAnnouncement.title,
          description: editingAnnouncement.description || null,
          auction_date: editingAnnouncement.auction_date || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingAnnouncement.id)
        .select()
        .single();

      if (error) throw error;

      setAnnouncements(announcements.map((a) => (a.id === editingAnnouncement.id ? data : a)));
      setEditingAnnouncement(null);
      toast({
        title: "Success",
        description: "Announcement updated successfully",
      });
    } catch (error) {
      console.error("Error updating announcement:", error);
      toast({
        title: "Error",
        description: "Failed to update announcement",
        variant: "destructive",
      });
    } finally {
      setAnnouncementLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    try {
      const { error } = await supabase.from("announcements").delete().eq("id", id);

      if (error) throw error;

      setAnnouncements(announcements.filter((a) => a.id !== id));
      toast({
        title: "Success",
        description: "Announcement deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting announcement:", error);
      toast({
        title: "Error",
        description: "Failed to delete announcement",
        variant: "destructive",
      });
    }
  };

  const handleAddListingToAnnouncement = (announcementId: string) => {
    navigate(`/add-listing?announcement=${announcementId}`);
  };

  const handleEditListing = (listingId: string) => {
    navigate(`/edit-listing/${listingId}`);
  };

  const handleDeleteListing = async (listingId: string, announcementId: string) => {
    try {
      const { error } = await supabase.from("listings").delete().eq("id", listingId).eq("breeder_id", user?.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Listing deleted successfully",
      });

      // Refresh the listings for this announcement
      window.location.reload();
    } catch (error) {
      console.error("Error deleting listing:", error);
      toast({
        title: "Error",
        description: "Failed to delete listing",
        variant: "destructive",
      });
    }
  };

  const AnnouncementListings: React.FC<{ announcementId: string }> = ({ announcementId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { data: listings = [], isLoading, error } = useAnnouncementListings({ announcementId });

    if (error) {
      console.error("Error loading announcement listings:", error);
      return null;
    }

    const transformedListings = listings.map((listing) => ({
      id: listing.id,
      title: listing.title,
      image:
        Array.isArray(listing.images) && listing.images.length > 0
          ? (listing.images[0] as string)
          : "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=300&fit=crop",
      price: listing.price ? `$${listing.price.toLocaleString()}` : undefined,
      location: listing.location || "Location not specified",
      breeder:
        listing.profiles?.business_name ||
        `${listing.profiles?.first_name || ""} ${listing.profiles?.last_name || ""}`.trim() ||
        "Unknown Breeder",
      breed: listing.breed,
      evaluationData: {
        calvingEase: listing.calving_ease,
        birthWeight: listing.birth_weight,
        weaningWeight: listing.weaning_weight,
        milk: listing.milk,
      },
    }));

    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-2 h-auto" disabled={isLoading}>
            <span className="text-sm text-gray-600">
              {isLoading ? "Loading listings..." : `View Listings (${listings.length})`}
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          {transformedListings.length === 0 ? (
            <div className="text-center text-gray-500 py-4 text-sm">No listings added to this announcement yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {transformedListings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  showEditButton={true}
                  onEdit={() => handleEditListing(listing.id)}
                  onDelete={() => handleDeleteListing(listing.id, announcementId)}
                />
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-col items-center gap-6 justify-center">
        <CardTitle className="text-center">Upcoming Auctions</CardTitle>
        <Button
          onClick={() => setShowAnnouncementForm(true)}
          className="bg-green-700 hover:bg-green-800"
          disabled={!subscriptionActive}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Announcement
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!authLoading && subscriptionActive === false && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">Your subscription is inactive. Subscribe to add or edit announcements.</p>
          </div>
        )}

        {showAnnouncementForm && (
          <Card className="border-green-200">
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium">New Announcement</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAnnouncementForm(false);
                    setNewAnnouncement({ title: "", description: "", auction_date: "" });
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="announcementTitle">Title *</Label>
                  <Input
                    id="announcementTitle"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                    placeholder="Enter announcement title..."
                  />
                </div>
                <div>
                  <Label htmlFor="announcementDescription">Description</Label>
                  <Textarea
                    id="announcementDescription"
                    value={newAnnouncement.description}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, description: e.target.value })}
                    placeholder="Enter announcement description..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="mr-3">Auction Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[240px] justify-start text-left font-normal",
                          !newAnnouncement.auction_date && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newAnnouncement.auction_date ? (
                          format(new Date(newAnnouncement.auction_date + "T00:00:00"), "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          newAnnouncement.auction_date
                            ? new Date(newAnnouncement.auction_date + "T00:00:00")
                            : undefined
                        }
                        onSelect={(date) =>
                          setNewAnnouncement({
                            ...newAnnouncement,
                            auction_date: date ? format(date, "yyyy-MM-dd") : "",
                          })
                        }
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleCreateAnnouncement}
                    disabled={announcementLoading || !newAnnouncement.title.trim()}
                    className="bg-green-700 hover:bg-green-800"
                  >
                    {announcementLoading ? "Creating..." : "Create Announcement"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAnnouncementForm(false);
                      setNewAnnouncement({ title: "", description: "", auction_date: "" });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {announcements.length === 0 && !showAnnouncementForm ? (
          <div className="text-center text-gray-500">
            <p>
              No announcements yet. Create your first announcement to share upcoming listings with potential buyers.
            </p>
          </div>
        ) : (
          announcements.map((announcement) => (
            <Card key={announcement.id} className="border-gray-200">
              <CardContent className="p-4">
                {editingAnnouncement?.id === announcement.id ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="editTitle">Title *</Label>
                      <Input
                        id="editTitle"
                        value={editingAnnouncement.title}
                        onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="editDescription">Description</Label>
                      <Textarea
                        id="editDescription"
                        value={editingAnnouncement.description || ""}
                        onChange={(e) =>
                          setEditingAnnouncement({ ...editingAnnouncement, description: e.target.value })
                        }
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="mr-3">Auction Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-[240px] justify-start text-left font-normal",
                              !editingAnnouncement.auction_date && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editingAnnouncement.auction_date ? (
                              format(new Date(editingAnnouncement.auction_date + "T00:00:00"), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={
                              editingAnnouncement.auction_date
                                ? new Date(editingAnnouncement.auction_date + "T00:00:00")
                                : undefined
                            }
                            onSelect={(date) =>
                              setEditingAnnouncement({
                                ...editingAnnouncement,
                                auction_date: date ? format(date, "yyyy-MM-dd") : null,
                              })
                            }
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleUpdateAnnouncement}
                        disabled={announcementLoading || !editingAnnouncement.title.trim()}
                        className="bg-green-700 hover:bg-green-800"
                      >
                        {announcementLoading ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button variant="outline" onClick={() => setEditingAnnouncement(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-lg">{announcement.title}</h4>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditingAnnouncement(announcement)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Announcement</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{announcement.title}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteAnnouncement(announcement.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    {announcement.description && <p className="text-gray-600 mb-3">{announcement.description}</p>}
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-sm text-gray-400">
                        {announcement.auction_date && (
                          <span className="text-foreground font-medium">
                            Auction:{" "}
                            {new Date(announcement.auction_date + "T00:00:00").toLocaleDateString("en-US", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                            {" • "}
                          </span>
                        )}
                        Created: {new Date(announcement.created_at).toLocaleDateString()}
                        {announcement.updated_at !== announcement.created_at && (
                          <> • Updated: {new Date(announcement.updated_at).toLocaleDateString()}</>
                        )}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddListingToAnnouncement(announcement.id)}
                        disabled={!subscriptionActive}
                        className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                      >
                        <ListPlus className="w-4 h-4 mr-2" />
                        Add Listing
                      </Button>
                    </div>

                    <AnnouncementListings announcementId={announcement.id} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </CardContent>
    </Card>
  );
};
